import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Accessibility, X, Type, Link2, TextCursorInput, AlignJustify,
  Contrast, Moon, Droplet, MousePointer2, Pause, RotateCcw, BookOpen, Plus, Minus
} from "lucide-react";

const STORAGE_KEY = "mh-acc-settings";

const defaults = {
  fontScale: 0,
  readableFont: false,
  highlightLinks: false,
  textSpacing: false,
  lineHeight: false,
  grayscale: false,
  highContrast: false,
  darkContrast: false,
  bigCursor: false,
  pauseAnim: false,
  readingGuide: false
};

const classFor = {
  readableFont: "acc-readable",
  highlightLinks: "acc-links",
  textSpacing: "acc-spacing",
  lineHeight: "acc-lineheight",
  grayscale: "acc-grayscale",
  highContrast: "acc-contrast",
  darkContrast: "acc-dark",
  bigCursor: "acc-cursor",
  pauseAnim: "acc-noanim"
};

const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(defaults);
  const [guidePos, setGuidePos] = useState(0);

  // load
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (saved) setSettings({ ...defaults, ...saved });
    } catch (e) { /* ignore */ }
  }, []);

  // apply
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(classFor).forEach(([key, cls]) => {
      root.classList.toggle(cls, !!settings[key]);
    });
    root.classList.remove("acc-font-1", "acc-font-2", "acc-font-3");
    if (settings.fontScale > 0) root.classList.add(`acc-font-${settings.fontScale}`);
    root.classList.toggle("acc-guide", !!settings.readingGuide);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)); } catch (e) { /* ignore */ }
  }, [settings]);

  // reading guide movement
  useEffect(() => {
    if (!settings.readingGuide) return;
    const onMove = (e) => setGuidePos(e.clientY);
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [settings.readingGuide]);

  const toggle = useCallback((key) => setSettings((s) => ({ ...s, [key]: !s[key] })), []);
  const setFont = (dir) => setSettings((s) => {
    let v = s.fontScale + dir;
    v = Math.max(0, Math.min(3, v));
    return { ...s, fontScale: v };
  });
  const reset = () => setSettings(defaults);

  const activeCount = Object.keys(defaults).filter((k) => (k === "fontScale" ? settings[k] > 0 : settings[k])).length;

  const Item = ({ icon: Ic, label, active, onClick }) => (
    <button className={`acc-item ${active ? "active" : ""}`} onClick={onClick} aria-pressed={active}>
      <Ic size={22} />
      <span>{label}</span>
    </button>
  );

  return (
    <>
      {settings.readingGuide && (
        <div className="acc-reading-guide" style={{ top: guidePos }} aria-hidden="true" />
      )}

      <button
        className="acc-fab"
        onClick={() => setOpen(true)}
        aria-label="Open accessibility menu"
        title="Accessibility Menu"
      >
        <Accessibility size={28} />
      </button>

      {open && <div className="acc-overlay" onClick={() => setOpen(false)} />}

      <aside className={`acc-panel ${open ? "open" : ""}`} role="dialog" aria-label="Accessibility options">
        <div className="acc-header">
          <div className="acc-header-title">
            <Accessibility size={22} />
            <span>Accessibility</span>
          </div>
          <button className="acc-close" onClick={() => setOpen(false)} aria-label="Close accessibility menu">
            <X size={22} />
          </button>
        </div>

        <div className="acc-body">
          <div className="acc-fontrow">
            <span>Text Size</span>
            <div className="acc-fontrow-ctrls">
              <button onClick={() => setFont(-1)} aria-label="Decrease text size" disabled={settings.fontScale === 0}><Minus size={16} /></button>
              <div className="acc-fontrow-level">
                {[1, 2, 3].map((n) => (<span key={n} className={settings.fontScale >= n ? "on" : ""} />))}
              </div>
              <button onClick={() => setFont(1)} aria-label="Increase text size" disabled={settings.fontScale === 3}><Plus size={16} /></button>
            </div>
          </div>

          <div className="acc-grid">
            <Item icon={Type} label="Readable Font" active={settings.readableFont} onClick={() => toggle("readableFont")} />
            <Item icon={Link2} label="Highlight Links" active={settings.highlightLinks} onClick={() => toggle("highlightLinks")} />
            <Item icon={TextCursorInput} label="Text Spacing" active={settings.textSpacing} onClick={() => toggle("textSpacing")} />
            <Item icon={AlignJustify} label="Line Height" active={settings.lineHeight} onClick={() => toggle("lineHeight")} />
            <Item icon={Contrast} label="High Contrast" active={settings.highContrast} onClick={() => toggle("highContrast")} />
            <Item icon={Moon} label="Dark Contrast" active={settings.darkContrast} onClick={() => toggle("darkContrast")} />
            <Item icon={Droplet} label="Grayscale" active={settings.grayscale} onClick={() => toggle("grayscale")} />
            <Item icon={MousePointer2} label="Big Cursor" active={settings.bigCursor} onClick={() => toggle("bigCursor")} />
            <Item icon={Pause} label="Pause Animations" active={settings.pauseAnim} onClick={() => toggle("pauseAnim")} />
            <Item icon={BookOpen} label="Reading Guide" active={settings.readingGuide} onClick={() => toggle("readingGuide")} />
          </div>

          <button className="acc-reset" onClick={reset} disabled={activeCount === 0}>
            <RotateCcw size={16} /> Reset All Settings {activeCount > 0 ? `(${activeCount})` : ""}
          </button>

          <Link to="/accessibility-statement" className="acc-statement-link" onClick={() => setOpen(false)}>
            View our Accessibility Statement
          </Link>

          <p className="acc-note">This tool conforms toward WCAG 2.1 Level AA. We welcome your feedback on any barriers you encounter.</p>
        </div>
      </aside>
    </>
  );
};

export default AccessibilityWidget;
